import { Injectable, inject } from '@angular/core';
import { GroupOfUser, GroupsService, UsersService } from '@era/security-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import {
  GroupApiActions,
  MembershipListApiActions,
  MembershipListUIActions,
  UserApiActions,
  UserGroupResolverActions,
} from './user-group.actions';

@Injectable()
export class UserGroupEffects {
  private readonly userService = inject(UsersService);
  private readonly groupService = inject(GroupsService);
  private readonly actions$ = inject(Actions);

  userGroupLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserGroupResolverActions.userGroupsRequested),
      mergeMap(({ userId }) =>
        safeMap(
          this.userService.getGroupsOfUser({ id: userId }),
          (group: GroupOfUser[]) => UserApiActions.userGroupsLoaded({ groups: group }),
          (error) => UserApiActions.userGroupsLoadedError({ error })
        )
      )
    );
  });

  userGroupDeletedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MembershipListUIActions.userGroupDeleted),
      mergeMap(({ group, user }) =>
        safeMap(
          this.groupService.removeUserFromGroup({ id: group.id, userId: user.id }),
          () => MembershipListApiActions.userGroupDeleted({ group, user }),
          (error) => GroupApiActions.userGroupsGroupDeletedError({ error })
        )
      )
    );
  });

  userGroupAddedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MembershipListUIActions.userGroupAdded),
      mergeMap(({ group, userToAdd }) =>
        safeMap(
          this.groupService.addUserToGroup({ id: group.id, memberModel: { id: userToAdd.id } }),
          () => MembershipListApiActions.userGroupAdded({ group, userToAdd }),
          (error) => GroupApiActions.userGroupAddedError({ error }),
          `Failed to add User Group for Group ID: '${group.id}' and User ID: '${userToAdd.id}'`
        ).pipe(
          mergeMap(() =>
            safeMap(
              this.userService.getGroupsOfUser({ id: userToAdd.id }),
              (groups: GroupOfUser[]) => UserApiActions.userGroupsLoaded({ groups }),
              (error) => UserApiActions.userGroupsLoadedError({ error }),
              `Failed to load User Groups for User ID: '${userToAdd.id}'`
            )
          )
        )
      )
    );
  });
}
